import { lazy, Suspense } from 'react';
import { Route, Switch } from 'react-router-dom';

import { LT_ROUTE } from 'bl/loggers.bl';
import PinLockedRoutes from 'components/core/PinLockedRoutes.component';
import ReloadChecker from 'components/core/ReloadChecker.component';
import { SignupRoutes } from 'components/core/SignupRoutes.component';
import UserMessages from 'components/core/UserMessages.component';
import VersionBanner from 'components/core/VersionBanner.component';
import Experiments from 'components/experiment/Experiments.component';
import Poc from 'components/experiment/poc/Poc.component';
import ForgotPasswordPage from 'components/login/ForgotPasswordPage.component';
import { NoAccessLandingPanel } from 'components/messages/NoAccessPanel.component';
import CONF from 'config/conf.singleton';
import FLAG from 'config/flag.singleton';
import LINK from 'constants/link.const';
import { useTitle } from 'hooks/useTitle.hook';
import fromAnyToBool from 'utils/converters/fromAnyToBool.util';
import { timeSub03days, timeSub30min } from 'utils/date/manipulate.util';
import { nowAsZulu } from 'utils/date/now.util';
import determineFbSalonId from 'utils/determineFbSalonId.util';
import { isEnvLab, isEnvNotProd, isEnvQA, isEnvStage } from 'utils/env/determine.util';
import logger from 'utils/logger.util';

import NotFound from 'lib/NotFoundComponent';
import PrivateRoute from 'lib/PrivateRoute';
import SalonRoute from 'lib/SalonRoute';
import NotificationContainer from 'notification/index';
import SalonLoaderContainer from 'onboarding/SalonLoader';
import Salon from 'salon/Salon.component';
import Login from 'user/components/Login';
import ResetPassword from 'user/components/ResetPassword';

import CN from './Application.module.scss';

const CTAG = 'Application';
const L = logger([CTAG, LT_ROUTE]);

const ReactQueryDevtools = lazy(() => import('@tanstack/react-query-devtools').then(($) => ({ default: $.ReactQueryDevtools })));

const href = window?.location?.href;

const SalonWithIdFromFacebook = () => {
  const now = nowAsZulu();
  const salonIdFromFb = determineFbSalonId();

  L.info('Opening salon with ID from Facebook:', salonIdFromFb, 'at:', now);
  return <Salon salonId={salonIdFromFb} />;
};

const LoginWithSalonIdFromHref = () => {
  const now = nowAsZulu();
  const salonIdFromHref = href.indexOf('state=') > 0 ? href.split('state=')[1] : void 1;

  L.info('Opening login with ID from href:', salonIdFromHref, 'at:', now);
  return <Login salonId={salonIdFromHref} />;
};

const RootRoutes = () => (
  <Switch>
    <Route path="/poc/" component={Poc} />

    <Route exact path={LINK.root} component={LoginWithSalonIdFromHref} />
    <Route exact path="/login" component={LoginWithSalonIdFromHref} />
    <Route path="/fbe-auth" component={LoginWithSalonIdFromHref} />
    <Route path="/login/forgot" component={ForgotPasswordPage} />
    <Route path="/login/forcereset" component={ResetPassword} />
    <Route path="/no-access" component={NoAccessLandingPanel} />
    <Route path="/signup" component={SignupRoutes} />

    <PrivateRoute exact path="/salon" component={SalonWithIdFromFacebook} />
    <SalonRoute path="/setup/salon/:id" component={SalonLoaderContainer} />
    <Route path="/salon/:id" component={PinLockedRoutes} />

    {fromAnyToBool(CONF.experiments) && <Route path="/x" component={Experiments} />}

    <Route component={NotFound} />
  </Switch>
);

const Application = () => {
  useTitle();

  // TODO: move this cutoff deeper, maybe even into the RELEASE singleton and use environment variables for it
  const cutoff = isEnvStage() || isEnvQA() || isEnvLab() ? timeSub30min : timeSub03days;

  const isQueryToolsRendered = isEnvNotProd() && fromAnyToBool(FLAG.reactQueryDevtools);
  const isMqRendered = fromAnyToBool(FLAG.mediaQ);

  return (
    <div data-bem="Application" className="root-container">
      {isMqRendered && (
        <p data-bem="Mq" className={CN.component}>
          mq
        </p>
      )}

      {isQueryToolsRendered && (
        <Suspense fallback={null}>
          <ReactQueryDevtools />
        </Suspense>
      )}

      <NotificationContainer />
      <VersionBanner />
      <UserMessages />
      <ReloadChecker tag={CTAG} cutoff={cutoff} />

      <RootRoutes />
    </div>
  );
};

export default Application;
